import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BrandM,
  BrandS,
  BrandXS,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { LightedBorder } from '@sorare/core/src/atoms/ui/LightedBorder';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsMediumDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsMediumDesktopAndAbove';
import { cardPull } from '@sorare/core/src/lib/glossary';
import {
  desktopAndAbove,
  laptopAndAbove,
  mediumDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { getClueAssets } from '#src/lib/crafting.ts';

import type { CraftCluesBalance_cardPullConfig } from './__generated__/index.graphql.ts';
import CLUE_PLACEHOLDER from './assets/clues_placeholder.webp';

const Clue = styled.div`
  display: flex;
  position: relative;

  gap: var(--half-unit);
  --lighted-border-color: var(--c-white);
  & ${LightedBorder} {
    display: none;
  }
  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: center;
    gap: var(--intermediate-unit);
    padding: var(--double-unit) var(--intermediate-unit) var(--triple-unit);
    & ${LightedBorder} {
      display: block;
      z-index: -1;
    }
  }
  @media ${desktopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Icon = styled.img`
  width: var(--double-unit);
  height: var(--double-unit);
  @media ${mediumDesktopAndAbove} {
    width: var(--triple-unit);
    height: var(--triple-unit);
  }
`;

const GrowLabelS = styled(LabelS)`
  flex-grow: 1;
`;

const CluesPlaceholder = styled.img`
  height: calc(6 * var(--unit));
`;

const MinWidthBrandXS = styled(BrandXS)`
  min-width: calc(7 * var(--unit));
  flex-shrink: 0;
  text-align: right;
`;

type Props = {
  cardPullConfigs: CraftCluesBalance_cardPullConfig[];
};

export const CraftCluesBalance = ({ cardPullConfigs }: Props) => {
  const { sport } = useSportContext();
  const cluesAssets = getClueAssets(sport);
  const { currentUser } = useCurrentUserContext();
  const isLaptopAndAbove = useIsLaptop();
  const isMediumDesktopAndAbove = useIsMediumDesktopAndAbove();
  const { craftClues } = currentUser ?? {};

  const { formatMessage } = useIntl();

  const clues = cardPullConfigs.at(0)?.revealCosts ?? [];

  if (isLaptopAndAbove && !isMediumDesktopAndAbove) {
    return (
      <Tooltip
        title={
          <Vertical>
            {clues.map(clue => {
              const cluesOfType = craftClues?.find(
                c => c.currency === clue.currency
              );
              return (
                <Horizontal key={clue.propertyName}>
                  <Icon
                    src={cluesAssets[clue.propertyName].iconUrl}
                    alt={formatMessage(cluesAssets[clue.propertyName].label)}
                  />
                  <GrowLabelS color="var(--c-nd-600)">
                    <FormattedMessage
                      {...cluesAssets[clue.propertyName].label}
                    />
                  </GrowLabelS>
                  <MinWidthBrandXS>
                    {cluesOfType?.amount ?? 0}/{cluesOfType?.cap ?? 10}
                  </MinWidthBrandXS>
                </Horizontal>
              );
            })}
          </Vertical>
        }
      >
        <Clue>
          <LightedBorder />
          <CluesPlaceholder src={CLUE_PLACEHOLDER} />
          <BrandM>
            <FormattedMessage {...cardPull.clues} />
          </BrandM>
        </Clue>
      </Tooltip>
    );
  }

  return (
    <Horizontal gap={isLaptopAndAbove ? 0 : 2}>
      {clues.map(clue => {
        const cluesOfType = craftClues?.find(c => c.currency === clue.currency);
        return (
          <Tooltip
            key={clue.currency}
            title={
              <Vertical gap={0.5} center style={{ maxWidth: 300 }}>
                <LabelL bold className="text-center">
                  <FormattedMessage {...cluesAssets[clue.propertyName].label} />
                </LabelL>
                <LabelM color="var(--c-nd-600)" className="text-center">
                  <FormattedMessage
                    {...cluesAssets[clue.propertyName].description}
                  />
                </LabelM>
              </Vertical>
            }
          >
            <Clue title={formatMessage(cluesAssets[clue.propertyName].label)}>
              <LightedBorder />
              <Icon
                src={cluesAssets[clue.propertyName].iconUrl}
                alt={formatMessage(cluesAssets[clue.propertyName].label)}
              />
              <BrandS>
                {cluesOfType?.amount ?? 0}/{cluesOfType?.cap ?? 10}
              </BrandS>
            </Clue>
          </Tooltip>
        );
      })}
    </Horizontal>
  );
};

CraftCluesBalance.fragments = {
  cardPullConfig: gql`
    fragment CraftCluesBalance_cardPullConfig on CardPullConfig {
      name
      revealCosts {
        propertyName
        currency
      }
    }
  ` as TypedDocumentNode<CraftCluesBalance_cardPullConfig>,
};
